import { Add, AttachMoney } from '@mui/icons-material';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import Title from 'lib/designSystem/Title';
import { formatMoney, validateProducts } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { OperativeProfileData } from '../index';
import GenerateLead from './GenerateLead';

const LabelValueRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
    >
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
};

const CreditLines = ({
  data,
  businessIdentifier,
  refetchCreditLines,
}: {
  data: OperativeProfileData;
  businessIdentifier: string | null;
  refetchCreditLines: () => Promise<void>;
}) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const showSgr = validateProducts(user, ['sgr']);
  const showCredit = validateProducts(user, ['credit']);
  const showConfirming = validateProducts(user, ['confirming']);
  const showFactoring = validateProducts(user, ['factoring']);
  const [openGenerateLead, setOpenGenerateLead] = useState(false);
  const [productTypeId, setProductTypeId] = useState<string | undefined>(
    undefined,
  );

  const handleOpenGenerateLead = (productType: string) => {
    setProductTypeId(productType);
    setOpenGenerateLead(true);
  };

  return (
    <>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
        }}
      >
        <Title
          title="Líneas de Crédito"
          icon={<AttachMoney color="primary" sx={{ fontSize: 20 }} />}
          color="primary"
        />

        <Grid container spacing={3}>
          {/* Línea de Crédito Factoring */}
          {showFactoring && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Factoring
                </Typography>
                {data.factoringCreditLine ? (
                  <>
                    <Typography
                      variant="h5"
                      color="primary.main"
                      fontWeight={700}
                      gutterBottom
                    >
                      {formatMoney(data.factoringCreditLine.amount)}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <LabelValueRow
                        label="Estado"
                        value={data.factoringCreditLine.status}
                      />
                      {data.factoringCreditLine.validUntil && (
                        <LabelValueRow
                          label="Válida hasta"
                          value={new Date(
                            data.factoringCreditLine.validUntil,
                          ).toLocaleDateString('es-CL')}
                        />
                      )}
                      <LabelValueRow
                        label="Utilizado"
                        value={`${formatMoney(data.factoringCreditLineUsedAmount)} (${(
                          (data.factoringCreditLineUsedAmount /
                            data.factoringCreditLine.amount) *
                          100
                        ).toFixed(2)}%)`}
                      />
                      <LabelValueRow
                        label="Disponible"
                        value={`${formatMoney(
                          data.factoringCreditLine.amount -
                            data.factoringCreditLineUsedAmount,
                        )} (${(
                          ((data.factoringCreditLine.amount -
                            data.factoringCreditLineUsedAmount) /
                            data.factoringCreditLine.amount) *
                          100
                        ).toFixed(2)}%)`}
                      />
                      <LabelValueRow label="Observaciones" value={''} />
                      <Typography variant="body2" color="text.secondary">
                        {data.factoringCreditLine.observations}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay línea de crédito disponible
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Línea de Crédito Confirming */}
          {showConfirming && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Confirming
                </Typography>
                {data.confirmingCreditLine ? (
                  <>
                    <Typography
                      variant="h5"
                      color="secondary.main"
                      fontWeight={700}
                      gutterBottom
                    >
                      {formatMoney(data.confirmingCreditLine.amount)}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <LabelValueRow
                        label="Estado"
                        value={data.confirmingCreditLine.status}
                      />
                      {data.confirmingCreditLine.validUntil && (
                        <LabelValueRow
                          label="Válida hasta"
                          value={new Date(
                            data.confirmingCreditLine.validUntil,
                          ).toLocaleDateString('es-CL')}
                        />
                      )}
                      <LabelValueRow
                        label="Utilizado"
                        value={`${formatMoney(data.confirmingCreditLineUsedAmount)} (
                ${(
                  (data.confirmingCreditLineUsedAmount /
                    data.confirmingCreditLine.amount) *
                  100
                ).toFixed(2)}%)`}
                      />
                      <LabelValueRow
                        label="Disponible"
                        value={`${formatMoney(
                          data.confirmingCreditLine.amount -
                            data.confirmingCreditLineUsedAmount,
                        )} (${(
                          ((data.confirmingCreditLine.amount -
                            data.confirmingCreditLineUsedAmount) /
                            data.confirmingCreditLine.amount) *
                          100
                        ).toFixed(2)}%)`}
                      />
                      <LabelValueRow label="Observaciones" value={''} />
                      <Typography variant="body2" color="text.secondary">
                        {data.confirmingCreditLine.observations}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay línea de crédito disponible
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Línea de Crédito Créditos */}
          {showCredit && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Créditos
                </Typography>
                {data.creditCreditLine ? (
                  <>
                    <Typography
                      variant="h5"
                      color="info.main"
                      fontWeight={700}
                      gutterBottom
                    >
                      {formatMoney(data.creditCreditLine.amount)}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <LabelValueRow
                        label="Estado"
                        value={data.creditCreditLine.status}
                      />
                      {data.creditCreditLine.validUntil && (
                        <LabelValueRow
                          label="Válida hasta"
                          value={new Date(
                            data.creditCreditLine.validUntil,
                          ).toLocaleDateString('es-CL')}
                        />
                      )}
                      <LabelValueRow
                        label="Utilizado"
                        value={`${formatMoney(data.creditCreditLineUsedAmount)} (
                ${(
                  (data.creditCreditLineUsedAmount /
                    data.creditCreditLine.amount) *
                  100
                ).toFixed(2)}%)`}
                      />
                      <LabelValueRow
                        label="Disponible"
                        value={`${formatMoney(
                          data.creditCreditLine.amount -
                            data.creditCreditLineUsedAmount,
                        )} (${(
                          ((data.creditCreditLine.amount -
                            data.creditCreditLineUsedAmount) /
                            data.creditCreditLine.amount) *
                          100
                        ).toFixed(2)}%)`}
                      />
                      <LabelValueRow label="Observaciones" value={''} />
                      <Typography variant="body2" color="text.secondary">
                        {data.creditCreditLine.observations}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay línea de crédito disponible
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Línea de Crédito SGR */}
          {showSgr && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Garantías
                </Typography>

                {data.sgrCreditLine ? (
                  <>
                    <Typography
                      variant="h5"
                      color="info.main"
                      fontWeight={700}
                      gutterBottom
                    >
                      {formatMoney(data.sgrCreditLine.amount)}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <LabelValueRow
                        label="Estado"
                        value={data.sgrCreditLine?.status}
                      />
                      {data.sgrCreditLine.validUntil && (
                        <LabelValueRow
                          label="Válida hasta"
                          value={new Date(
                            data.sgrCreditLine.validUntil,
                          ).toLocaleDateString('es-CL')}
                        />
                      )}
                      <LabelValueRow
                        label="Utilizado"
                        value={`${formatMoney(data.sgrCreditLineUsage.totalUsage)} (
                ${(
                  (data.sgrCreditLineUsage.totalUsage /
                    data.sgrCreditLine.amount) *
                  100
                ).toFixed(2)}%)`}
                      />
                      <LabelValueRow
                        label="Disponible"
                        value={`${formatMoney(
                          data.sgrCreditLine.amount -
                            data.sgrCreditLineUsage.totalUsage,
                        )} (${(
                          ((data.sgrCreditLine.amount -
                            data.sgrCreditLineUsage.totalUsage) /
                            data.sgrCreditLine.amount) *
                          100
                        ).toFixed(2)}%)`}
                      />
                      <LabelValueRow label="Observaciones" value={''} />
                      <Typography variant="body2" color="text.secondary">
                        {data.sgrCreditLine.observations}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Subconcentraciones
                      </Typography>
                      <LabelValueRow
                        label="Fiel Cumplimiento"
                        value={`${formatMoney(data.sgrCreditLineUsage.faithfulComplianceUsage)} de ${formatMoney(
                          data.sgrCreditLine.subProducts?.find(
                            (sub) => sub.name === 'Fiel cumplimiento',
                          )?.amount || 0,
                        )}`}
                      />
                      <LabelValueRow
                        label="Seriedad de la Oferta"
                        value={`${formatMoney(data.sgrCreditLineUsage.offerSeriousnessUsage)} de ${formatMoney(
                          data.sgrCreditLine.subProducts?.find(
                            (sub) => sub.name === 'Seriedad de oferta',
                          )?.amount || 0,
                        )}`}
                      />
                      <LabelValueRow
                        label="Anticipo"
                        value={`${formatMoney(data.sgrCreditLineUsage.advanceUsage)} de ${formatMoney(
                          data.sgrCreditLine.subProducts?.find(
                            (sub) => sub.name === 'Anticipo',
                          )?.amount || 0,
                        )}`}
                      />
                      <LabelValueRow
                        label="Canje de retenciones"
                        value={`${formatMoney(data.sgrCreditLineUsage.withholdingExchangeUsage)} de ${formatMoney(
                          data.sgrCreditLine.subProducts?.find(
                            (sub) => sub.name === 'Canje de retenciones',
                          )?.amount || 0,
                        )}`}
                      />
                    </Box>
                  </>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    onClick={() => handleOpenGenerateLead('sgr')}
                    role="button"
                    tabIndex={0}
                    sx={{
                      cursor: 'pointer',
                      '&:hover .empty-text': { textDecoration: 'underline' },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenGenerateLead('sgr');
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="empty-text"
                    >
                      No hay línea de garantía disponible
                    </Typography>
                    <Tooltip title="Generar lead">
                      <IconButton
                        size="small"
                        aria-label="Generar lead"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenGenerateLead('sgr');
                        }}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: '#fff',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        }}
                      >
                        <Add sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
      <GenerateLead
        open={openGenerateLead}
        onClose={() => setOpenGenerateLead(false)}
        productTypeId={productTypeId}
        businessIdentifier={businessIdentifier}
        refetchCreditLines={refetchCreditLines}
      />
    </>
  );
};

export default CreditLines;
